"use client";

import * as React from "react";
import { ShoppingCart, Trash2, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@baluu/ui/components/sheet";
import { Button } from "@baluu/ui/components/button";
import { ScrollArea } from "@baluu/ui/components/scroll-area";
import { BasketItemCard } from "./basket-item-card";
import {
  type BasketUnavailability,
  type BasketUnavailabilityBucket,
  getUnavailabilityBucket,
} from "@obby/lib";

type BasketItem = {
  id: string;
  type: "booking" | "product" | "giftcard" | "bundle";
  title: string;
  subtitle?: string;
  imageUrl?: string;
  price: number;
  quantity: number;
  currency?: string;
  sessionDate?: string;
  sessionTime?: string;
  timezone?: string;
  isOnline?: boolean;
  location?: string;
  tickets?: Array<{
    name: string;
    quantity: number;
    price: number;
    lineTotal?: number;
    /** Host-supplied line text; see BasketItemCardProps.tickets[].label. */
    label?: string;
  }>;
  addOns?: Array<{ name: string; quantity: number; price: number; groupName?: string | null }>;
  /**
   * Line-level charges that sit OUTSIDE `price` — today postage and gift wrap
   * on product lines. Add-ons are already inside the item price, so they are
   * displayed but not re-added; these are added to the drawer total, matching
   * the checkout page and the PaymentIntent.
   */
  surcharges?: Array<{ name: string; amount: number }>;
  isAvailable?: boolean;
  availabilityMessage?: string;
  unavailability?: BasketUnavailability;
  priceChanged?: boolean;
  currentPrice?: number;
  redeemedFromBundleName?: string;
};

type BasketDrawerProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  items: BasketItem[];
  currency?: string;
  isLoading?: boolean;
  isValidating?: boolean;
  hasUnavailableItems?: boolean;
  hasPriceChangedItems?: boolean;
  checkoutBlockedMessage?: string;
  noticeMessage?: string;
  onRemoveItem?: (id: string) => void;
  onUpdateItemPrice?: (id: string, newPrice: number) => void;
  onReduceQuantity?: (id: string, newQuantity: number) => void;
  onRetryValidation?: (id: string) => void;
  onJoinMembership?: (id: string) => void;
  onSeeAlternatives?: (id: string) => void;
  /** Called when the user clicks the bulk "Remove all unavailable" action. */
  onRemoveAllUnavailable?: () => void;
  onClearBasket?: () => void;
  onCheckout?: () => void;
  onContinueShopping?: () => void;
  checkoutButtonText?: string;
  continueShoppingText?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  className?: string;
  portalContainer?: HTMLElement | null;
  /**
   * Stacking level of the drawer and its backdrop. Defaults to 4000 (above
   * popups, below a checkout overlay); a host raises it to open the drawer
   * over its checkout overlay.
   */
  zIndex?: number;
};

/** Makes the items list's scroll content take the drawer's width (see the
 *  ScrollArea below). Exported for the test that pins it. */
const BASKET_SCROLL_VIEWPORT_FIT =
  "[&_[data-radix-scroll-area-viewport]>div]:!block";

function formatPrice(price: number, currency: string = "GBP"): string {
  try {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(price / 100);
  } catch {
    return `${currency} ${(price / 100).toFixed(2)}`;
  }
}

function BasketDrawer({
  isOpen,
  onOpenChange,
  items,
  currency = "GBP",
  isLoading = false,
  isValidating = false,
  hasUnavailableItems = false,
  hasPriceChangedItems = false,
  checkoutBlockedMessage,
  noticeMessage,
  onRemoveItem,
  onUpdateItemPrice,
  onReduceQuantity,
  onRetryValidation,
  onJoinMembership,
  onSeeAlternatives,
  onRemoveAllUnavailable,
  onClearBasket,
  onCheckout,
  onContinueShopping,
  checkoutButtonText = "Checkout",
  continueShoppingText = "Continue shopping",
  emptyStateTitle = "Your basket is empty",
  emptyStateDescription = "Browse our classes and products to add items to your basket.",
  className,
  portalContainer,
  zIndex = 4000,
}: BasketDrawerProps) {
  const isEmpty = items.length === 0;

  // Calculate total (use current price if available and price changed).
  // Surcharges are per-LINE amounts already scaled by the caller — never
  // multiply them by quantity here.
  const total = items.reduce((sum, item) => {
    const itemPrice =
      item.priceChanged && item.currentPrice !== undefined
        ? item.currentPrice
        : item.price;
    const surchargeTotal = (item.surcharges ?? []).reduce(
      (s, sur) => s + sur.amount,
      0
    );
    return sum + itemPrice * item.quantity + surchargeTotal;
  }, 0);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Bucket-aware summary state. Partial-capacity items don't hard-block
  // checkout (user can accept the auto-reduction); transient errors also
  // don't block (validate poll re-fires). Everything else (sold-out,
  // cancelled, gone, members-only, school-error) is a hard blocker.
  const bucketCounts = new Map<BasketUnavailabilityBucket, number>();
  let hasHardBlocker = false;
  // Buckets `sold_out` with the other eligibility gates, but "sold out" is
  // the wrong headline for it — the class has room, the buyer's allowance
  // doesn't. Counted separately so the banner can say so.
  let trialLimitCount = 0;
  for (const item of items) {
    if (item.unavailability) {
      const b = getUnavailabilityBucket(item.unavailability);
      bucketCounts.set(b, (bucketCounts.get(b) ?? 0) + 1);
      if (item.unavailability.code === "TRIAL_LIMIT_REACHED") trialLimitCount += 1;
      if (b !== "partial" && b !== "transient") hasHardBlocker = true;
    } else if (item.isAvailable === false) {
      // Legacy unstructured unavailable: treat as a hard blocker.
      hasHardBlocker = true;
    }
  }
  const totalAffected = Array.from(bucketCounts.values()).reduce(
    (a, b) => a + b,
    0,
  );
  const isCheckoutBlocked = hasHardBlocker || !!checkoutBlockedMessage;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn("flex flex-col w-full sm:max-w-md", className)}
        // Inline, not a z-[…] class: the level is a runtime value. Inline
        // style also outranks the primitives' own z-50 classes.
        overlayStyle={{ zIndex }}
        style={{ zIndex }}
        container={portalContainer}
      >
        <SheetHeader className="px-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Your Basket
              {itemCount > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({itemCount} {itemCount === 1 ? "item" : "items"})
                </span>
              )}
            </SheetTitle>
            {items.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5"
                onClick={onClearBasket}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Validation loading indicator */}
        {isValidating && (
          <div className="flex items-center gap-2 py-2 px-3 bg-muted rounded-md">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">
              Checking availability...
            </span>
          </div>
        )}

        {/* Bucket-aware summary banner. Tone matches the dominant bucket so a
            basket with only "low stock" items shows yellow, not red. */}
        {!isValidating && totalAffected > 0 && (() => {
          const entries = Array.from(bucketCounts.entries());
          const allPartial = entries.length === 1 && entries[0]![0] === "partial";
          const allMembers = entries.length === 1 && entries[0]![0] === "members_only";
          const allTransient = entries.length === 1 && entries[0]![0] === "transient";

          let title = `${totalAffected} ${totalAffected === 1 ? "item needs" : "items need"} attention`;
          if (entries.length === 1) {
            const [b, n] = entries[0]!;
            if (b === "partial") title = "Low availability";
            else if (b === "sold_out" && trialLimitCount === n)
              title = n === 1 ? "Trial limit reached" : `Trial limit reached on ${n} items`;
            else if (b === "sold_out" && trialLimitCount > 0)
              title = `${n} items need attention`; // sold-out and trial mixed
            else if (b === "sold_out")
              title = `${n} ${n === 1 ? "item" : "items"} sold out`;
            else if (b === "cancelled")
              title = `${n} ${n === 1 ? "item was" : "items were"} cancelled`;
            else if (b === "expired") title = "Some items have expired";
            else if (b === "gone") title = "Some items are no longer offered";
            else if (b === "members_only") title = "Members-only items in your basket";
            else if (b === "school_error") title = "We couldn't process this checkout";
            else if (b === "transient") title = "We couldn't check availability";
          }

          const containerClass = allPartial
            ? "bg-amber-100/60 border-amber-200 text-amber-900"
            : allMembers
              ? "bg-blue-100/60 border-blue-200 text-blue-900"
              : allTransient
                ? "bg-amber-100/60 border-amber-200 text-amber-900"
                : "bg-destructive/10 border-destructive/20 text-destructive";

          const showRemoveAll =
            !allPartial && !allMembers && !allTransient && !!onRemoveAllUnavailable;

          return (
            <div className={cn("flex items-start gap-2 py-2 px-3 border rounded-md", containerClass)}>
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">{title}</p>
              </div>
              {showRemoveAll && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={onRemoveAllUnavailable}
                >
                  Remove all
                </Button>
              )}
            </div>
          );
        })()}

        {/* Generic checkout blocker (e.g. mixed-school basket) */}
        {checkoutBlockedMessage && !isValidating && (
          <div className="flex items-start gap-2 py-2 px-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{checkoutBlockedMessage}</p>
          </div>
        )}

        {noticeMessage && !isValidating && (
          <div className="flex items-start gap-2 py-2 px-3 bg-amber-100/60 border border-amber-200 rounded-md">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{noticeMessage}</p>
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-hidden -mx-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <h3 className="font-semibold text-foreground">{emptyStateTitle}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {emptyStateDescription}
              </p>
              {onContinueShopping && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    onContinueShopping();
                    onOpenChange(false);
                  }}
                >
                  {continueShoppingText}
                </Button>
              )}
            </div>
          ) : (
            // Radix renders the viewport's child as `display: table` (it
            // sizes to its content so horizontal scrolling works), which
            // ignores the viewport's width: a card whose content can't wrap
            // (the `truncate` title, which only truncates inside a definite
            // width) widened the table past the drawer, and the viewport
            // clipped the card's right edge and line price on narrow screens.
            // The drawer only scrolls vertically, so lay the child out as a
            // block that takes the viewport's width.
            <ScrollArea className={cn("h-full px-4", BASKET_SCROLL_VIEWPORT_FIT)}>
              <div className="space-y-3 py-2">
                {items.map((item) => (
                  <BasketItemCard
                    key={item.id}
                    id={item.id}
                    type={item.type}
                    title={item.title}
                    subtitle={item.subtitle}
                    imageUrl={item.imageUrl}
                    price={item.price}
                    quantity={item.quantity}
                    currency={item.currency || currency}
                    sessionDate={item.sessionDate}
                    sessionTime={item.sessionTime}
                    timezone={item.timezone}
                    isOnline={item.isOnline}
                    location={item.location}
                    tickets={item.tickets}
                    addOns={item.addOns}
                    surcharges={item.surcharges}
                    isAvailable={item.isAvailable}
                    availabilityMessage={item.availabilityMessage}
                    unavailability={item.unavailability}
                    priceChanged={item.priceChanged}
                    currentPrice={item.currentPrice}
                    redeemedFromBundleName={item.redeemedFromBundleName}
                    onRemove={onRemoveItem}
                    onUpdatePrice={onUpdateItemPrice}
                    onReduceQuantity={onReduceQuantity}
                    onRetryValidation={onRetryValidation}
                    onJoinMembership={onJoinMembership}
                    onSeeAlternatives={onSeeAlternatives}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Footer with total and checkout */}
        {!isEmpty && (
          <SheetFooter className="border-t pt-4 px-0 flex-col gap-3">
            {/* Price changed notice */}
            {hasPriceChangedItems && !hasUnavailableItems && (
              <div className="text-sm text-warning text-center">
                Some prices have changed. Review and update to continue.
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center w-full">
              <span className="text-lg font-semibold">Total</span>
              <span className="text-xl font-bold">
                {formatPrice(total, currency)}
              </span>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 w-full">
              <Button
                size="lg"
                className="w-full"
                onClick={onCheckout}
                disabled={isCheckoutBlocked || isValidating}
              >
                {isValidating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                {checkoutButtonText}
              </Button>

              {onContinueShopping && (
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full"
                    onClick={onContinueShopping}
                  >
                    {continueShoppingText}
                  </Button>
                </SheetClose>
              )}
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { BasketDrawer, BASKET_SCROLL_VIEWPORT_FIT };
export type { BasketDrawerProps, BasketItem as BasketDrawerItem };
